import { useState, useEffect } from "react";
import { Cookie } from "lucide-react";
import { triggerHaptic } from "@/utils/haptics";
import { useIsMobile } from "@/hooks/use-mobile";

interface CookieConsentData {
  essential: boolean;
  analytics: boolean;
  timestamp: number;
  version: string;
}

const COOKIE_POLICY_VERSION = "1.0";
const CONSENT_STORAGE_KEY = "sendfeedback_cookie_consent";

export const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    const storedConsent = localStorage.getItem(CONSENT_STORAGE_KEY);

    if (!storedConsent) {
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    } else {
      try {
        const consent: CookieConsentData = JSON.parse(storedConsent);
        if (consent.version !== COOKIE_POLICY_VERSION) {
          setShowBanner(true);
        }
      } catch {
        setShowBanner(true);
      }
    }
  }, []);

  useEffect(() => {
    if (!showBanner) return;
    const timer = setTimeout(() => {
      const firstButton = document.querySelector('[data-cookie-action]') as HTMLElement;
      firstButton?.focus();
    }, 100);
    return () => clearTimeout(timer);
  }, [showBanner]);

  const saveConsent = (analytics: boolean) => {
    const consent: CookieConsentData = {
      essential: true,
      analytics,
      timestamp: Date.now(),
      version: COOKIE_POLICY_VERSION,
    };

    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(consent));
    window.dispatchEvent(new CustomEvent('cookie-consent-updated', { detail: consent }));
    setShowBanner(false);
  };

  const handleAcceptAll = () => {
    if (isMobile) triggerHaptic('success');
    saveConsent(true);
  };

  const handleEssentialOnly = () => {
    if (isMobile) triggerHaptic('light');
    saveConsent(false);
  };

  if (!showBanner) return null;

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      data-cookie-banner
      className="fixed bottom-[env(safe-area-inset-bottom,0px)] left-3 right-3 mb-[72px] md:mb-0 md:left-auto md:right-5 md:bottom-5 md:max-w-sm z-50 rounded-3xl border border-border/60 bg-card/90 backdrop-blur-md shadow-[0_18px_42px_-14px_rgba(62,92,77,0.28)] animate-in slide-in-from-bottom-2 fade-in duration-200"
    >
      <div className="px-4 py-3.5 flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-start sm:items-center gap-3 flex-1">
          <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/12 text-primary ring-1 ring-primary/20">
            <Cookie className="h-4 w-4" />
          </span>
          <p className="text-xs text-foreground/75 flex-1 leading-snug">
            We use cookies for auth &amp; analytics.{" "}
            <a
              href="/cookie-policy"
              className="text-primary underline decoration-primary/30 hover:decoration-primary underline-offset-2"
            >
              Learn more
            </a>
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <button
            data-cookie-action
            onClick={handleEssentialOnly}
            className="flex-1 sm:flex-none h-10 rounded-full border border-border/60 bg-background/70 backdrop-blur px-4 text-xs font-medium text-foreground/75 hover:text-foreground hover:border-foreground/30 transition-colors"
          >
            Decline
          </button>
          <button
            data-cookie-action
            onClick={handleAcceptAll}
            className="flex-1 sm:flex-none h-10 rounded-full bg-foreground text-background px-4 text-xs font-medium shadow-[0_8px_24px_-10px_rgba(62,92,77,0.45)] hover:bg-foreground/90 transition-all"
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  );
};

// Helper function to check if analytics cookies are consented
export const hasAnalyticsCookieConsent = (): boolean => {
  try {
    const storedConsent = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!storedConsent) return false;

    const consent: CookieConsentData = JSON.parse(storedConsent);
    return consent.analytics === true && consent.version === COOKIE_POLICY_VERSION;
  } catch {
    return false;
  }
};

// Helper function to reopen cookie settings
export const openCookieSettings = () => {
  localStorage.removeItem(CONSENT_STORAGE_KEY);
  window.location.reload();
};
